// Single source of truth for "which project are we loading?" — used by
// every editor surface (canvas, preview, future embeds). Resolves in
// priority order:
//
//   0. Preview-subdomain hostname: `{owner}--{key}.preview.symbols.app`
//      (or `…preview.localhost` for local dev) — when it names a project it
//      OWNS owner + key; the query below cannot override it (D2-iv).
//   1. Explicit URL params: `?owner=…&key=…[&env=…&branch=…&version=…]`
//   2. (the preview hostname again — only reached when it named nothing)
//   3. Workspace-scoped pathname: `/w/{org}/p/{key}` or `/w/{org}/{ws}/p/{key}`
//      (scheme A canvas-project form — owner IS the org segment; see
//      ./projectUrls.js). Any other `/w/…` shape is an intranet route and
//      never yields an owner/key pair.
//   4. Canvas-style pathname: `/{owner}/{key}/…` (excluding reserved
//      chrome routes like `/dashboard`, `/signin`, etc.)
//
// Each source is tried independently — the first non-empty value wins
// per field. Callers can pass their own `reservedPathSegments` set when
// their surface has different chrome routes from canvas; default covers
// the current canvas set.

import { parseWorkspaceProjectPath } from './projectUrls.js'

// FA204 / frank trap — DEFAULT_RESERVED_PATHS/PREVIEW_SUBDOMAIN_RE/
// EDIT_DOMAIN/fromParams/fromHost/fromPath moved from module scope into
// this file's sole export (grep confirms no external consumer of the
// former `DEFAULT_RESERVED_PATHS` export either — see longPress.js for
// the identical self-contained-local shape).
export function resolveProjectParams({
  loc = typeof window !== 'undefined' ? window.location : null,
  reservedPathSegments
} = {}) {
  if (!loc) {
    return { owner: null, key: null, env: null, branch: null, version: null, sources: {} }
  }

  const DEFAULT_RESERVED_PATHS = new Set([
    'workspaces',
    'dashboard',
    'signin',
    'signup',
    'signup-confirmation',
    'reset-password',
    'waitlist'
  ])

  // Matches the preview-host shapes:
  //   {owner}--{key}.preview.symbols.app           (prod)
  //   {owner}--{key}.<tier>.preview.symbols.app    (tier — next/dev/staging/upcoming/...)
  //   {owner}--{key}.preview.localhost             (local)
  //   {owner}--{key}.<tier>.preview.localhost      (local tier — rare)
  // The optional `<tier>.` segment lives between the project slug and the
  // literal `preview.` apex, so each tier can serve its own preview surface
  // without tier-specific URL rewriters.
  const PREVIEW_SUBDOMAIN_RE =
    /^([^.]+)--([^.]+)(?:\.[a-z0-9-]+)?\.preview\.(symbols\.app|localhost)$/

  function fromParams(params) {
    return {
      owner: params.get('owner') || null,
      key: params.get('key') || null,
      env: params.get('env') || null,
      branch: params.get('branch') || null,
      version: params.get('version') || null
    }
  }

  function fromHost(hostname) {
    const match = hostname.match(PREVIEW_SUBDOMAIN_RE)
    if (match) return { owner: match[1], key: match[2] }
    return {}
  }

  function fromPath(pathname, reserved) {
    const segs = pathname.split('/').filter(Boolean)
    if (!segs.length) return {}

    // Workspace-scoped scheme A: `/w/<org>[/<ws>]/p/<key>` — the org segment is
    // the owner. Every OTHER `/w/…` shape is an intranet route: return {} (not
    // fall-through), otherwise the generic branch below misreads `/w/<org>/…`
    // as owner `w`, key `<org>`.
    if (segs[0] === 'w') {
      const wsProject = parseWorkspaceProjectPath(pathname)
      return wsProject ? { owner: wsProject.owner, key: wsProject.key } : {}
    }

    // Canvas-style: first two segments unless the first is a reserved
    // chrome route (dashboard / signin / etc.).
    if (segs.length >= 2 && !reserved.has(segs[0])) {
      return { owner: segs[0], key: segs[1] }
    }
    return {}
  }

  // Fallback moved off the destructuring default — same signature-drift
  // reason as authGuard.js's DEFAULT_AUTH_PATHS / minimizedSlots.js's
  // MINIMIZED_SLOTS_CAP.
  const resolvedReservedPathSegments = reservedPathSegments || DEFAULT_RESERVED_PATHS
  const params = new URLSearchParams(loc.search)
  const p = fromParams(params)
  const h = fromHost(loc.hostname || '')
  const pa = fromPath(loc.pathname || '', resolvedReservedPathSegments)

  // A preview host that names a project (`<owner>--<key>.preview…`) IS that
  // project's public page: it owns owner + key outright, and a `?owner&key`
  // query never overrides it — not both fields, not one (D2-iv,
  // PREVIEW-HOST-PUBLIC-ONLY). env / branch / version still come from the query.
  if (h.owner && h.key) {
    return {
      owner: h.owner,
      key: h.key,
      env: p.env || null,
      branch: p.branch || null,
      version: p.version || null,
      sources: { params: p, host: h, path: pa }
    }
  }

  return {
    owner: p.owner || h.owner || pa.owner || null,
    key: p.key || h.key || pa.key || null,
    env: p.env || null,
    branch: p.branch || null,
    version: p.version || null,
    sources: { params: p, host: h, path: pa }
  }
}
